import { DocletHostMedia, MediaBlockProps } from '../../../../doclets/types'
import { playableMedia } from '../../../../doclets/blocks/media/playable'
import { VIMEO_TRANSCODE_STATUS_MAP } from '../../../media/constants/media'

// The parts of a platform host media record (from useMediaUploader, a pasted
// link, or the hostMediaUpdated subscription) the Media block uses
export interface HostMediaLike {
  id?: string | null
  hostId?: string | null
  host?: string | null
  type?: string | null
  url?: string | null
  transcode?: string | null
}

/**
 * The Media block's props for a host media record. A video still
 * transcoding on Vimeo has no playable file yet, so `src` stays null until a
 * later update completes it. The caption is kept; the thumbnail only if it's
 * still the same media.
 */
export const propsFromHostMedia = (
  current: MediaBlockProps,
  media: HostMediaLike
): MediaBlockProps => {
  const sameMedia = !!media.hostId && media.hostId === current.hostId
  return {
    ...current,
    kind: media.type === 'video' ? 'video' : 'audio',
    host: media.host === 'vimeo' ? 'vimeo' : 'cloudinary',
    hostId: media.hostId ?? null,
    hostMediaId: media.id ?? null,
    src: media.transcode === 'processing' ? null : media.url || null,
    poster: sameMedia ? current.poster : null
  }
}

// Whether the block holds a video Vimeo hasn't finished yet, going by its
// host media record when the builder has it
export const isAwaitingVideo = (
  props: MediaBlockProps,
  records: Record<string, DocletHostMedia>
) =>
  props.kind === 'video' &&
  !!props.hostMediaId &&
  playableMedia(props, records).processing

export interface VimeoState {
  // Transcoding finished and there's a file to play
  complete: boolean
  // Vimeo couldn't transcode it: waiting longer won't help
  failed: boolean
  url: string | null
  width: number | null
  height: number | null
  duration: number | null
  // A real frame, once Vimeo has one (not its generic placeholder)
  thumbnail: string | null
}

/**
 * What a Vimeo video-details response says about a video the block is
 * waiting for. The file is chosen as data-extraction chooses it
 * (mediaHelpers.ts): the one at the video's own size, which is its full
 * resolution, else the first. Vimeo shows a generic placeholder picture
 * (`default_picture`) until it has generated a real frame, often before
 * transcoding ends.
 */
export const readVimeoState = (details: any): VimeoState => {
  const files: any[] = details?.files ?? []
  const file =
    files.find(
      f => f?.width === details?.width && f?.height === details?.height
    ) ?? files[0]
  const url = file?.link || null
  const pictures = details?.pictures
  return {
    complete:
      VIMEO_TRANSCODE_STATUS_MAP[details?.transcode?.status] === 'complete' &&
      !!url,
    failed: VIMEO_TRANSCODE_STATUS_MAP[details?.transcode?.status] === 'error',
    url,
    width: details?.width ?? null,
    height: details?.height ?? null,
    duration: details?.duration ?? null,
    thumbnail:
      pictures?.base_link && !pictures.default_picture
        ? pictures.base_link
        : null
  }
}

/**
 * The Cloudinary account a pasted link names, when it's a different one from
 * this environment's. The link check looks files up in this environment's
 * account only, so a link to another account (say a production file on a dev
 * environment) can't be added even though it plays in the browser. Null for
 * links that aren't res.cloudinary.com links, or when the account matches.
 */
export const otherCloudinaryAccount = (
  url: string,
  ourAccount: string | null | undefined
): string | null => {
  if (!ourAccount) return null
  let parsed: URL
  try {
    parsed = new URL(url.trim())
  } catch {
    return null
  }
  if (parsed.hostname.toLowerCase() !== 'res.cloudinary.com') return null
  const account = parsed.pathname.split('/')[1]
  return account && account.toLowerCase() !== ourAccount.toLowerCase()
    ? account
    : null
}

/**
 * Whether a Vimeo response status means the video is gone for good: deleted
 * (404, 410) or no longer ours to see (403). Anything else that isn't OK
 * (network trouble, rate limits, Vimeo errors, a missing token locally) is
 * worth asking again.
 */
export const isVimeoVideoGone = (status: number) =>
  status === 403 || status === 404 || status === 410

/**
 * MI6's audio size limit, when this file is over it (null otherwise, and for
 * video, which goes to Vimeo without one). Checked before uploading, so the
 * author is told the file is too large rather than to try again.
 */
export const audioSizeLimitExceeded = (
  file: { type: string; size: number },
  // As MI6's config returns it, which may be a numeric string
  audioLimit: number | string | null | undefined
): number | null => {
  const limit = Number(audioLimit)
  return file.type.startsWith('audio/') && limit > 0 && file.size > limit
    ? limit
    : null
}
